import {
  popupStore,
  userInfoStore,
  roundInfoStore,
  userCountStore,
  gameResultStore,
  gameConfigStore,
  applicationStore,
  userWinCountStore,
  freeBetStore,
  lobbyStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  DefaultQueryParams,
  isMobile,
  loadingStore,
  notificationStore,
  parseQuery,
  RoundStatus,
  tableBetHistoryStore,
} from '@pascal/pascal-core';

import { PopUps } from '@pascal/pascal-core/table-line/t_types/enums/PopupTypes';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';
import { gameStore } from '../store/gameStore';
import { roundTimeStore } from '../store/roundTimeStore';

import { worker } from '../index';
import { CARDS } from '../enums/cardTypes';
import { roundsWinBetTypes } from '../store/roundsWinBetTypes';
import { gameLiveConnection } from './GameConnection';
import { doBetStore } from '../store/doBetStore';
import { cardsStore } from '../store/cardsStore';
import { soundService, soundTypes } from './SoundService';
import { winningAmountStore } from '../store/winningAmountStore';
import { limitsAndPayoutsStore } from '../store/limitsAndPayoutsStore';
import {
  BET_SHOP_GAME_ID,
  NOTIFICATION_TEXT,
  PASCAL_PLATFORM_ID,
  SHUFFLED_MESSAGE,
} from '../enums/constants';
import { liveConnection } from '../core';
import { MobileShuffleAnimation, WebShuffleAnimation } from '../core/constants/events';

let timeOutId;

const animationsTimeOutId = [];
let shuffleAnimationTimeOutId;
let endShuffleAnimationTimeOutId;
let burnedCardsAnimationTimeOutId;
let endBurnedCardsAnimationTimeOutId;

class GameService {
  onGetInitialStateResult({
    rate,
    cards,
    limits,
    balance,
    precision,
    roundInfo,
    lastResults,
    currentBets,
    currencyCode,
    gameConfiguration,
    burnedCardsInfo,
    gameTypeInfo,
    isTestPlatform,
    userBets,
    userFreebets,
  }) {
    freeBetStore.setIsFreeChip(true);
    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
    const { [DefaultQueryParams.PartnerId]: partnerId, [DefaultQueryParams.GameId]: gameId } =
      parseQuery(window.location.search);
    const isBetShop = +gameId === BET_SHOP_GAME_ID;
    if (!isBetShop) {
      if (+partnerId === PASCAL_PLATFORM_ID) {
        applicationStore.setIsTestPlatform(true);
      } else {
        applicationStore.setIsTestPlatform(isTestPlatform);
      }
      userCountStore.setUsersCount(currentBets.length || 0);
    }
    this.handleResetDataInitial();
    const roomName = gameConfiguration?.partnerGameConfig
      ? gameConfiguration.partnerGameConfig?.gameTypes?.find(
          (item) => item.gameTypeId === gameConfigStore.gameTypeId
        ).config.room
      : '';
    lobbyStore.setRoomName(roomName);
    if (!isBetShop) {
      const alreadyOpenedCards = cards
        .filter(
          ({ card, roundId }) =>
            roundInfo &&
            card !== CARDS.CUTTING_CARD &&
            roundId !== roundInfo?.roundId &&
            roundId !== 0
        )
        .reverse();
      const nextCards = cards
        .filter(({ roundId }) => !roundId || (roundInfo && roundId === roundInfo?.roundId))
        .reverse();
      const { limitsAndPayouts } = gameConfiguration;
      cardsStore.setOpenedCardsCount(alreadyOpenedCards.length);
      limitsAndPayoutsStore.setLimitsAndPayouts(limitsAndPayouts);
      gameResultStore.setGameResults(nextCards);
      gameResultStore.setAdditionalResults(alreadyOpenedCards);
      roundInfoStore.setRoundInfo({
        ...gameTypeInfo,
        roundId: roundInfo ? roundInfo.roundId : null,
      });
      gameConfigStore.setLimits(limits);
      applicationStore.setIsTestPlatform(isTestPlatform);
      cardsStore.setCuttingCardPlace(gameTypeInfo.cuttingCardPlace);
      doBetStore.setQuickBets(limits.quickBets);
      if (!freeBetStore.activeFreeBet.id) {
        doBetStore.setSelectedChip(limits.quickBets[0]);
      }
    } else {
      roundInfoStore.setRoundInfo({ ...roundInfo, roundId: roundInfo ? roundInfo.roundId : null });
    }
    roundsWinBetTypes.setWinBetTypes(lastResults || []);
    if (roundInfo && RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      const time = Math.floor(Math.abs(roundInfo.second));
      roundTimeStore.setTime(time);
      roundTimeStore.setBetShopTime(time);
      this.sendWorker(time);
    }
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });
    if (!isBetShop) {
      userCountStore.setUsersCount(currentBets.length);
    }
    if (roundInfo && roundInfo.second < 0 && !+gameTypeInfo.shuffledSeconds) {
      if (userBets.length && !isBetShop) {
        if (userBets[0].freeBetId) {
          freeBetStore.setActivateFreeBet(
            userFreebets.find((item) => item.id === userBets[0].freeBetId)
          );
        }
        doBetStore.setBetsModel({
          betModel: userBets[0].betData.map((item) => {
            const { winAmount, ...all } = item;
            return all;
          }),
        });
      }
      gameStore.setIsBetsClosed(true);
      if (!isBetShop && gameTypeInfo?.cuttingCardPlace < 0) {
        cardsStore.setIsCuttingCardOpen(true);
      }
      roundInfo.roundCards.forEach((openedCard) => {
        cardsStore.updateTableCards(openedCard);
      });
      cardsStore.setBankerTotalPoints(roundInfo.roundData?.bankerTotalPoint);
      cardsStore.setPlayerTotalPoints(roundInfo.roundData?.playerTotalPoint);
    }
    if (roundInfo && roundInfo.status === RoundStatus.OnFinish && !isBetShop) {
      if (userBets.length) {
        doBetStore.setBetsModel({
          betModel: userBets[0].betData,
        });
        winningAmountStore.setWinningAmount(userBets[0].winAmount);
        winningAmountStore.setLastWin(userBets[0].winAmount);
        if (roundInfo.roundData) {
          winningAmountStore.setWinType(roundInfo.roundData.winType);
          winningAmountStore.setIsPairWin(roundInfo.roundData.isPairWin);
        }
        setTimeout(() => {
          this.handleResetEndedRoundData();
        }, 5000);
      }
      const winingUsers = currentBets.filter((user) => user.winAmount !== 0);
      if (winingUsers.length) {
        winingUsers.sort((a, b) => b.winAmount - a.winAmount);
        userWinCountStore.setUserWiningBets(winingUsers);
      }
    }
    if (gameTypeInfo?.shuffledSeconds && !isBetShop) {
      const { shuffledSeconds } = gameTypeInfo;
      gameStore.setIsBetsClosed(true);
      this.handleResetEndedRoundData();
      this.handleShuffleAnimationInitial({ burnedCardsInfo, shuffledSeconds });
    }
    popupStore.close();
  }

  handleDoBetResult(data) {
    const { amount } = data;
    const { isDemo } = applicationStore;
    const { userId } = userInfoStore;
    const { balance, setDemoBalance } = userInfoStore;
    userCountStore.setUsersCount(
      data.betData.length ? userCountStore.usersCount + 1 : userCountStore.usersCount
    );
    if (data.userId === +userId) {
      doBetStore.setReBet(data.betData.map(({ winAmount, ...all }) => all));
      doBetStore.setReBetAmount(amount);
      if (userSettingsStore.isSoundOn) {
        soundService.playBetAccepted();
      }
      if (isDemo) {
        setDemoBalance(balance - amount);
      }
      notificationStore.setNotificationType(data.responseCode);
      setTimeout(() => notificationStore.setNotificationType(null), 1000);
    }
  }

  handleDoBetResultValidation(data) {
    doBetStore.resetBetsModels();
    notificationStore.setNotificationType(data.responseCode);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
  }

  handleRoundStart({ data: { startedRound } }) {
    userCountStore.setUsersCount(0);
    roundInfoStore.setRoundId(startedRound.roundId);
    this.handleFilterResultsOpenedCards();
    this.sendWorker(roundInfoStore.duration);
    this.handleResetEndedRoundData();
    gameStore.setIsBetsClosed(false);
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const isBetShop = +gameId === BET_SHOP_GAME_ID;
    if (isBetShop) {
      roundTimeStore.setBetShopTime(startedRound.duration);
    }
    if (cardsStore.isShuffle || cardsStore.burnedCards.length) {
      if (animationsTimeOutId.length) {
        animationsTimeOutId.forEach((id) => {
          clearTimeout(id);
        });
        animationsTimeOutId.length = 0;
      }
      cardsStore.setOpenedCardsCount(0);
      cardsStore.setBurnedCards([]);
      cardsStore.setBurnedCardsCount(0);
    }
  }

  handleResultReport({ data: { resultReportData } }) {
    const {
      openedCard,
      currentCardPoint,
      bankerTotalPoints,
      playerTotalPoints,
      cuttingCardPlace,
      message,
      cardsResult,
    } = resultReportData;
    if (openedCard) {
      soundService.play(soundTypes.Card);
      cardsStore.updateTableCards(openedCard);
      cardsStore.setCurrentCardPoint(currentCardPoint);
      cardsStore.setBankerTotalPoints(bankerTotalPoints);
      cardsStore.setPlayerTotalPoints(playerTotalPoints);
      cardsStore.setCuttingCardPlace(cuttingCardPlace);
    }
    if (message === SHUFFLED_MESSAGE) {
      const lastAndNextResults = cardsResult.reverse().reduce(
        (acc, cur) => {
          if (cur.roundId) {
            acc.alreadyOpenedCards.push(cur);
          } else {
            acc.nextCards.push(cur);
          }
          return acc;
        },
        {
          alreadyOpenedCards: [],
          nextCards: [],
        }
      );
      gameResultStore.setGameResults(lastAndNextResults.nextCards);
      gameResultStore.setAdditionalResults(lastAndNextResults.alreadyOpenedCards);
      gameStore.setIsBetsClosed(true);
      this.handleResetEndedRoundData();
      this.handleShuffleStarting(resultReportData);
    }
  }

  handleRoundFin({ data: { finishedRound, bets } }) {
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const isBetShop = +gameId === BET_SHOP_GAME_ID;
    const { roundData } = finishedRound;
    const { userId } = userInfoStore;
    const { freeBetTotalWinCalculator } = freeBetStore;

    if (!isBetShop) {
      const currentUserBet = bets.find((bet) => +bet.userId === +userId);
      if (currentUserBet) {
        doBetStore.setBetsModel({
          betModel: currentUserBet.additionalParams,
        });
        gameLiveConnection.getBalance();
        if (currentUserBet.freeBetId) {
          freeBetTotalWinCalculator(currentUserBet);
        }
        winningAmountStore.setWinningAmount(currentUserBet.winAmount);
        winningAmountStore.setLastWin(currentUserBet.winAmount);
        setTimeout(() => {
          if (!applicationStore.isDemo) {
            tableBetHistoryStore.handleBetHistoryUpdate();
          }
        }, 5000);
      }
    }
    roundsWinBetTypes.setWinBetTypes([
      ...roundsWinBetTypes.winBetTypes,
      { ...finishedRound, roundId: finishedRound.id },
    ]);
    winningAmountStore.setWinType(roundData.winType);
    winningAmountStore.setIsPairWin(roundData.isPairWin);
    if (!isBetShop) {
      const winingUsers = bets.filter((user) => user.winAmount !== 0);
      if (winingUsers.length) {
        winingUsers.sort((a, b) => b.winAmount - a.winAmount);
        userWinCountStore.setUserWiningBets(winingUsers);
      }
    }
  }

  handleBetsClosed({ data: { gameTypeId, roundId } }) {
    const { activeFreeBet } = freeBetStore;
    gameStore.setIsBetsClosed(true);
    gameStore.setIsNotification(true);
    gameStore.setNotificationText(NOTIFICATION_TEXT.NO_MORE_BETS);

    setTimeout(() => {
      gameStore.setNotificationText(NOTIFICATION_TEXT.DEALING_TIME);
      setTimeout(() => {
        gameStore.setNotificationText('');
        gameStore.setIsNotification(false);
      }, 2000);
    }, 2000);

    const { betsModels } = doBetStore;
    const betData = betsModels[betsModels.length - 1]?.betModel;
    if (betData) {
      gameLiveConnection.doBet(
        activeFreeBet.id
          ? {
              betData,
              gameTypeId,
              roundId,
              freeBetId: activeFreeBet.id,
            }
          : {
              betData,
              gameTypeId,
              roundId,
            }
      );
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      // popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  handleReconnecting(error) {
    if (error && error.message) {
      // eslint-disable-next-line
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect();
      this.socketRecursiveReconnecting();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    if (popupStore.popupName !== PopUps.ConnectionError) {
      popupStore.toggleReconnect();
      popupStore.close();
    }
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      // popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  handleFilterResultsOpenedCards() {
    const alreadyOpenedCards = gameResultStore.gameResults.filter(({ roundId }) => roundId !== 0);
    gameResultStore.setAdditionalResults([
      ...alreadyOpenedCards,
      ...gameResultStore.additionalResults,
    ]);
    const results = [...gameResultStore.gameResults];
    results.length -= alreadyOpenedCards.length;
    gameResultStore.setGameResults(results);
  }

  handleResetEndedRoundData() {
    cardsStore.resetCardsInfo();
    doBetStore.resetBetsModels();
    winningAmountStore.setWinType(null);
    winningAmountStore.setIsPairWin(false);
    winningAmountStore.setWinningAmount(null);
    cardsStore.setIsCuttingCardOpen(false);
    userWinCountStore.setUserWiningBets(null);
  }

  handleShuffleStarting({ newShuffledCards, burnedCardsInfo }) {
    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    // shuffle animation start
    let animationDuration = 0;
    for (let i = 0; i < animData.length; i += 1) {
      shuffleAnimationTimeOutId = setTimeout(() => {
        cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
      animationDuration += animData[i].payload.duration;
    }

    endShuffleAnimationTimeOutId = setTimeout(() => {
      gameResultStore.setGameResults(newShuffledCards.reverse());
      gameResultStore.setAdditionalResults([]);
      roundsWinBetTypes.setWinBetTypes([]);
      cardsStore.setOpenedCardsCount(0);
    }, animationDuration);
    animationsTimeOutId.push(endShuffleAnimationTimeOutId);
    animationDuration += 1000;
    // shuffle animation end

    // burned cards animation start
    cardsStore.setBurnedCardsCount(burnedCardsInfo.burnedCardsCount);
    for (let i = 0; i < burnedCardsInfo.burnedCardsCount + 1; i += 1) {
      animationDuration += 500;
      burnedCardsAnimationTimeOutId = setTimeout(() => {
        soundService.play(soundTypes.Card);
        cardsStore.setShuffleAnimationType('');
        cardsStore.setIsShuffle(false);
        cardsStore.updateBurnedCards(i === 0 ? burnedCardsInfo.firstBurnedCard : null);
      }, animationDuration);
      animationsTimeOutId.push(burnedCardsAnimationTimeOutId);
    }
    animationDuration += 1000;

    endBurnedCardsAnimationTimeOutId = setTimeout(() => {
      cardsStore.setBurnedCards([]);
      cardsStore.setBurnedCardsCount(0);
    }, animationDuration);
    animationsTimeOutId.push(endBurnedCardsAnimationTimeOutId);
    // burned cards animation start
  }

  handleShuffleAnimationInitial({ burnedCardsInfo, shuffledSeconds }) {
    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    // shuffle animation start
    let animationDuration = 0;
    let skipAnimationSecond = 0;
    for (let i = 0; i < animData.length; i += 1) {
      skipAnimationSecond += animData[i].payload.duration;
      if (shuffledSeconds * 1000 + 700 < skipAnimationSecond) {
        shuffleAnimationTimeOutId = setTimeout(() => {
          cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
        }, animationDuration);
        animationsTimeOutId.push(shuffleAnimationTimeOutId);
        const skipTime = animData[i].payload.duration - (skipAnimationSecond - shuffledSeconds);
        animationDuration += skipTime < 0 ? animData[i].payload.duration : skipTime;
      }
    }

    if (shuffledSeconds * 1000 < skipAnimationSecond) {
      animationDuration += 1000;
      endShuffleAnimationTimeOutId = setTimeout(() => {
        gameResultStore.setAdditionalResults([]);
        roundsWinBetTypes.setWinBetTypes([]);
        cardsStore.setOpenedCardsCount(0);
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
      skipAnimationSecond += 1000;
    }
    // shuffle animation end

    // burned cards animation start
    cardsStore.setBurnedCardsCount(burnedCardsInfo.burnedCardsCount);
    if (shuffledSeconds * 1000 < skipAnimationSecond) {
      animationDuration += 700;
      for (let i = 0; i < burnedCardsInfo.burnedCardsCount + 1; i += 1) {
        animationDuration += 300;
        burnedCardsAnimationTimeOutId = setTimeout(() => {
          soundService.play(soundTypes.Card);
          cardsStore.setShuffleAnimationType('');
          cardsStore.setIsShuffle(false);
          cardsStore.updateBurnedCards(i === 0 ? burnedCardsInfo.firstBurnedCard : null);
        }, animationDuration);
        animationsTimeOutId.push(burnedCardsAnimationTimeOutId);
      }
      animationDuration += 1000;
      endBurnedCardsAnimationTimeOutId = setTimeout(() => {
        cardsStore.setBurnedCards([]);
        cardsStore.setBurnedCardsCount(0);
      }, animationDuration);
      animationsTimeOutId.push(endBurnedCardsAnimationTimeOutId);
    } else {
      cardsStore.setBurnedCardsCount(burnedCardsInfo.burnedCardsCount);
      for (let i = 0; i < burnedCardsInfo.burnedCardsCount + 1; i += 1) {
        cardsStore.setShuffleAnimationType('');
        cardsStore.setIsShuffle(false);
        cardsStore.updateBurnedCards(i === 0 ? burnedCardsInfo.firstBurnedCard : null);
      }
    }
  }

  handleReplay(repliedBetData) {
    gameStore.setIsBetsClosed(true);
    doBetStore.setQuickBets([0.5, 2.5, 5.0, 10.0, 50.0, 100.0]);
    doBetStore.setBetsModel({
      betModel: repliedBetData.additionalParams.map((item) => {
        const { winAmount, ...all } = item;
        return all;
      }),
    });
    setTimeout(() => {
      repliedBetData.roundData.cards.forEach((openedCard, i) => {
        setTimeout(() => {
          cardsStore.updateTableCards(openedCard);
        }, i * 500);
      });
    }, 1000);
    setTimeout(() => {
      cardsStore.setBankerTotalPoints(repliedBetData.roundData.bankerTotalPoint);
      cardsStore.setPlayerTotalPoints(repliedBetData.roundData.playerTotalPoint);
    }, 3500);
    setTimeout(() => {
      doBetStore.setBetsModel({
        betModel: repliedBetData.additionalParams,
      });
      winningAmountStore.setWinningAmount(repliedBetData.winAmount);
      winningAmountStore.setWinType(repliedBetData.roundData.winType);
      winningAmountStore.setIsPairWin(repliedBetData.roundData.isPairWin);
    }, 4500);
  }

  handleResetDataInitial() {
    if (animationsTimeOutId.length) {
      animationsTimeOutId.forEach((id) => {
        clearTimeout(id);
      });
      animationsTimeOutId.length = 0;
    }
    cardsStore.setBurnedCardsCount(0);
    winningAmountStore.setLastWin(0);
    doBetStore.resetBetsModels();
    doBetStore.setReBet(null);
    doBetStore.setReBetAmount(0);
    cardsStore.resetCardsInfo();
    winningAmountStore.setWinType(null);
    winningAmountStore.setIsPairWin(false);
    winningAmountStore.setWinningAmount(null);
    roundsWinBetTypes.setWinBetTypes([]);
    cardsStore.setBurnedCards([]);
    cardsStore.setIsShuffle(false);
    cardsStore.setShuffleAnimationType('');
    cardsStore.setOpenedCardsCount(0);
    gameResultStore.setAdditionalResults([]);
    cardsStore.setIsCuttingCardOpen(false);
    gameStore.setIsBetsClosed(false);
  }
}

export const gameService = new GameService();
